import { currentRoute, describe } from "./identify";
import { Recorder } from "./recorder";

// Setup verification. Loading the app with ?afb-selftest=<nonce> files one feedback item and
// one error carrying the nonce, so an agent can call the check_setup MCP tool and see exactly
// what data reached the backend.

export async function runSelfTest(opts: {
  endpoint: string;
  key: string;
  attributes: string[];
  nonce: string;
  context: () => Record<string, unknown>;
  reporter: () => string;
  captureError: (source: string, message: string, stack?: string) => void;
  flush: () => void;
  /** Also send a screenshot, to prove capture works on this site (unless screenshots are off). */
  screenshots: boolean;
  exclude: Element | null;
}) {
  // Test the element identification on the most specific tagged element, else the main content.
  const tagged = document.querySelectorAll(opts.attributes.map((a) => `[${a}]`).join(","));
  const el = tagged[tagged.length - 1] ?? document.querySelector("main") ?? document.body.firstElementChild ?? document.body;
  const form = new FormData();
  form.append("key", opts.key);
  form.append(
    "payload",
    JSON.stringify({
      comment: `Agentic Feedback self-test ${opts.nonce}`,
      url: location.href,
      route: currentRoute(),
      target: describe(el, opts.attributes),
      context: { ...opts.context(), microphoneAllowedByPolicy: Recorder.allowedByPolicy(), screenshotsEnabled: opts.screenshots },
      reporter: opts.reporter(),
    }),
  );
  if (opts.screenshots) {
    try {
      const mod = await import(/* @vite-ignore */ `${opts.endpoint}/sdk-capture.js`);
      const r = el.getBoundingClientRect();
      const shot: Blob | null = await mod.captureViewport({
        exclude: opts.exclude ?? document.createElement("i"),
        outlines: [{ x: r.left, y: r.top, w: r.width, h: r.height }],
      });
      if (shot) form.append("screenshot", shot, "screen.jpg");
    } catch (err) {
      console.warn(`[agentic-feedback] self-test ${opts.nonce}: screenshot failed`, err);
    }
  }
  const error = new Error(`Agentic Feedback self-test error ${opts.nonce}`);
  opts.captureError("selftest", error.message, error.stack);
  opts.flush();
  try {
    const res = await fetch(`${opts.endpoint}/v1/feedback`, { method: "POST", body: form });
    console.info(`[agentic-feedback] self-test ${opts.nonce}: feedback ${res.ok ? "sent" : `failed (${res.status})`}, error sent`);
  } catch (err) {
    console.warn(`[agentic-feedback] self-test ${opts.nonce}: could not reach ${opts.endpoint}`, err);
  }
}
